import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import {
  Activity,
  
  BarChart3,
  Bell,
  CalendarDays,
  ChevronRight,
  Crown,
  Dumbbell,
  Home,
  LogOut,
  Rocket,
  Scale,
  ShieldCheck,
  Sparkles,

  TrendingUp,
  User,
  Video,
} from "lucide-react";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import useUserRoles from "@/hooks/useUserRoles";
import { PROGRAM_IDS } from "@/lib/programIds";

export type DashboardTabId = "program" | "overview" | "progress" | "measurements" | "profile";

type NavItem =
  | { kind: "tab"; id: DashboardTabId; label: string; icon: typeof Rocket }
  | { kind: "route"; to: string; label: string; icon: typeof Rocket; programId?: string };

type NavGroup = {
  id: string;
  label: string;
  items: NavItem[];
};

interface Props {
  /** Dashboard dışındaki sayfalarda verilmez; sekmeler linke dönüşür. */
  activeTab?: DashboardTabId;
  onTabChange?: (tab: DashboardTabId) => void;
}

const DashboardSidebar = ({ activeTab, onTabChange }: Props) => {
  const { t } = useTranslation();
  const { signOut, hasProgramAccess } = useAuth();
  const { isAdmin, isModerator } = useUserRoles();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { state, setOpenMobile, isMobile } = useSidebar();
  const collapsed = state === "collapsed" && !isMobile;
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  // Dashboard dışından mount edildiğinde aktif sekmeyi URL'den türet.
  const currentTab: DashboardTabId | undefined =
    activeTab ?? ((searchParams.get("tab") as DashboardTabId | null) ?? undefined);

  // Rota değişiminde mobil offcanvas menüyü otomatik kapat.
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setOpenMobile(false);
  }, [pathname, searchParams, setOpenMobile]);

  const groups: NavGroup[] = useMemo(
    () => [
      {
        id: "panel",
        label: t("dashboard.nav.panel", "Panelim"),
        items: [
          { kind: "tab", id: "program", label: t("journey.tab", "Yolculuğum"), icon: Rocket },
          { kind: "tab", id: "overview", label: t("dashboard.overview", "Genel Bakış"), icon: Activity },
          { kind: "tab", id: "progress", label: t("dashboard.progress", "İlerleme"), icon: TrendingUp },
          { kind: "tab", id: "measurements", label: t("dashboard.measurements", "Ölçümler"), icon: Scale },
          { kind: "tab", id: "profile", label: t("dashboard.profile", "Profil"), icon: User },
        ],
      },
      {
        id: "training",
        label: t("dashboard.nav.training", "Antrenman"),
        items: [
          { kind: "route", to: "/programs", label: t("nav.programs", "Programlar"), icon: Dumbbell },
          { kind: "route", to: "/program/mobility", label: "Mobility & Control", icon: Sparkles, programId: PROGRAM_IDS.foundation },
          { kind: "route", to: "/program/strength-in-range", label: "Strength in Range", icon: Sparkles, programId: PROGRAM_IDS.strength },
          { kind: "route", to: "/program/functional-resilience", label: "Functional Resilience", icon: Sparkles, programId: PROGRAM_IDS.resilience },
        ],
      },
      {
        id: "fuel",
        label: "Movement Fuel",
        items: [
          
          { kind: "route", to: "/fitness-analysis", label: t("nav.analysis", "Fitness Analizi"), icon: BarChart3 },
          {
            kind: "route",
            to: "/nutrition-insights",
            label: t("nav.nutritionInsights", "Beslenme & Antrenman"),
            icon: TrendingUp,
          },
        ],
      },
      {
        id: "account",
        label: t("dashboard.nav.account", "Hesap"),
        items: [
          { kind: "route", to: "/appointments", label: t("nav.appointments", "Randevular"), icon: CalendarDays },
          { kind: "route", to: "/notifications", label: t("nav.notifications", "Bildirimler"), icon: Bell },
          { kind: "route", to: "/premium", label: t("nav.premium", "Üyelik"), icon: Crown },
        ],
      },
      ...(isAdmin || isModerator
        ? [
            {
              id: "management",
              label: t("dashboard.nav.management", "Yönetim"),
              items: [
                {
                  kind: "route" as const,
                  to: "/admin",
                  label: isAdmin
                    ? t("nav.admin", "Yönetim Paneli")
                    : t("nav.moderation", "Moderasyon Paneli"),
                  icon: ShieldCheck,
                },
              ],
            },
          ]
        : []),
    ],
    [t, isAdmin, isModerator],
  );


  const isRouteActive = (to: string) => pathname === to || pathname.startsWith(`${to}/`);
  const resolvedTab: DashboardTabId = currentTab ?? "program";
  const isItemActive = (item: NavItem) =>
    item.kind === "tab"
      ? pathname === "/dashboard" && resolvedTab === item.id
      : isRouteActive(item.to);

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      groups.map((g) => [g.id, g.id === "panel" || g.items.some((i) => i.kind === "route" && isRouteActive(i.to))]),
    ),
  );

  const toggleGroup = (id: string) =>
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));

  const closeMobile = () => {
    if (isMobile) setOpenMobile(false);
  };

  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      setSigningOut(false);
      setConfirmSignOut(false);
      setOpenMobile(false);
      navigate("/", { replace: true });
    }
  };

  return (
    <Sidebar
      collapsible="icon"
      className="md:top-16 md:h-[calc(100svh-4rem)] border-r border-border/40"
    >
      {/* Mobilde alt menü barının (MobileBottomNav) arkasında kalan öğeler için güvenli alan */}
      <SidebarContent className="pt-3 pb-[calc(6rem+env(safe-area-inset-bottom))] md:pb-3">
        {groups.map((group) => {
          const open = collapsed ? true : openGroups[group.id];
          const groupHasActive = group.items.some(isItemActive);
          return (
            <SidebarGroup key={group.id} className="py-1">
              {!collapsed && (
                <SidebarGroupLabel asChild>
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.id)}
                    aria-expanded={open}
                    className="flex w-full items-center justify-between rounded-md px-2 text-[11px] uppercase tracking-wider text-sidebar-foreground/60 hover:text-sidebar-foreground transition-colors"
                  >
                    <span className={groupHasActive ? "text-primary" : undefined}>{group.label}</span>
                    <motion.span animate={{ rotate: open ? 90 : 0 }} transition={{ duration: 0.2 }}>
                      <ChevronRight className="h-3.5 w-3.5" />
                    </motion.span>
                  </button>
                </SidebarGroupLabel>
              )}

              <AnimatePresence initial={false}>
                {open && (
                  <motion.div
                    key="content"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.22, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <SidebarGroupContent>
                      <SidebarMenu>
                        {group.items.map((item) => {
                          const active = isItemActive(item);
                          const Icon = item.icon;
                          const hasPackage =
                            item.kind === "route" &&
                            item.programId &&
                            hasProgramAccess(item.programId);
                          return (
                            <SidebarMenuItem key={item.kind === "tab" ? item.id : item.to}>
                              <SidebarMenuButton
                                asChild={item.kind === "route"}
                                isActive={active}
                                tooltip={item.label}
                                onClick={
                                  item.kind === "tab"
                                    ? () => {
                                        if (onTabChange) onTabChange(item.id);
                                        else navigate(`/dashboard?tab=${item.id}`);
                                        closeMobile();
                                      }
                                    : undefined
                                }
                                className={
                                  active
                                    ? "relative before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-full before:bg-primary"
                                    : undefined
                                }
                              >
                                {item.kind === "route" ? (
                                  <Link to={item.to} onClick={closeMobile}>
                                    <Icon />
                                    <span className="flex-1 truncate">{item.label}</span>
                                    {hasPackage && !collapsed && (
                                      <span
                                        title={t("subscription.activePackage", "Aktif paket")}
                                        className="flex items-center gap-1 rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold text-primary"
                                      >
                                        <Crown className="h-3 w-3" />
                                        {t("subscription.active", "Aktif")}
                                      </span>
                                    )}
                                    {hasPackage && collapsed && (
                                      <span className="absolute right-1 top-1.5 h-1.5 w-1.5 rounded-full bg-primary" />
                                    )}
                                  </Link>
                                ) : (
                                  <>
                                    <Icon />
                                    <span>{item.label}</span>
                                  </>
                                )}
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          );
                        })}
                      </SidebarMenu>
                    </SidebarGroupContent>
                  </motion.div>
                )}
              </AnimatePresence>
            </SidebarGroup>
          );
        })}
      </SidebarContent>

      <SidebarFooter className="border-t border-border/40">
        {collapsed ? (
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip={t("nav.home", "Ana Sayfa")}>
                <Link to="/">
                  <Home />
                  <span>{t("nav.home", "Ana Sayfa")}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton onClick={() => setConfirmSignOut(true)} tooltip={t("nav.logout")}>
                <LogOut />
                <span>{t("nav.logout")}</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        ) : (
          <div className="rounded-xl border border-border/50 bg-sidebar-accent/40 p-3 space-y-2">
            <Button asChild variant="secondary" size="sm" className="w-full justify-start gap-2">
              <Link to="/" onClick={closeMobile}>
                <Home className="h-4 w-4" />
                {t("nav.home", "Ana Sayfa")}
              </Link>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start gap-2"
              onClick={() => setConfirmSignOut(true)}
            >
              <LogOut className="h-4 w-4" />
              {t("nav.logout")}
            </Button>
          </div>
        )}
      </SidebarFooter>

      <AlertDialog open={confirmSignOut} onOpenChange={setConfirmSignOut}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("nav.logoutConfirmTitle", "Çıkış yapılsın mı?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "nav.logoutConfirmDesc",
                "Oturumun kapatılacak ve ana sayfaya yönlendirileceksin.",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={signingOut}>{t("common.cancel", "Vazgeç")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleSignOut();
              }}
              disabled={signingOut}
            >
              {t("nav.logout")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sidebar>
  );
};

export default DashboardSidebar;
